<script setup lang="ts">
/**
 * MY — Exempt Private Company Certificate (Section 260 & 261, M32016) 문서-뷰 패널.
 * Certificate (s.260) + Statement by Auditors (s.261).
 */
import { ref, reactive, computed, provide, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail, SignerEntry } from '@/features/AI-documents/types/docGen.types';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { labelize, typeOf, EPC_REQUIRED } from './exemptPrivateCompany/epcFields';
import { EPC_DOC_CTX } from './exemptPrivateCompany/epcContext';
import { buildEpcVariables, emptyEpcState, type EpcState } from '../mappers/exemptPrivateCompanyMapper';
import { formatMyAddressLine, composeMyAddressLine, parseMalaysianAddress, isEmptyMyAddress } from '@/shared/utils/malaysianAddress';
import { fetchEntityAddresses, formatMyCompanyRegNo } from '@/shared/composables/useEntityAddresses';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import AddressInputModal from './cdd/AddressInputModal.vue';
import EpcCertificateDoc from './exemptPrivateCompany/EpcCertificateDoc.vue';
import EpcAuditorsStatementDoc from './exemptPrivateCompany/EpcAuditorsStatementDoc.vue';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: Record<string, any> | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: { fieldGroup: string; index: number } | null;
  editTaskId?: number | null;
  studio?: boolean;
  appliedAddress?: { key: string; line: string; town: string; state: string; postcode: string; country: string; seq: number } | null;
  pendingPick?: { value: string; seq: number } | null;
  chatFormData?: Record<string, any> | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const router = useRouter();
const entityCd = computed(() => String(props.entityData?.entityCd || props.entityData?.icpCd || '').trim());

const taskTitleLocal = ref('');
const titleModel = computed({
  get: () => taskTitleLocal.value || props.taskTitle || '',
  set: (v: string) => { taskTitleLocal.value = v; },
});
const effectiveTitle = computed(() => titleModel.value.trim());

const state = reactive<EpcState>(emptyEpcState());
const activeKey = ref('');
const inputKey = ref('');
const inputText = ref('');
const dateMenu = ref(false);

function setFieldValue(key: string, val: string) {
  state.values[key] = typeOf(key) === 'address' ? formatMyAddressLine(val) : val;
}

function defaultRoleForKey(key: string): string | null {
  if (key === 'director_name') return 'DIRECTOR';
  if (key === 'secretary_name') return 'SECRETARY';
  // MY AUDITOR register = 감사회사 → Firm Name 필드에 연결
  if (key === 'audit_firm_name') return 'AUDITOR';
  return null;
}

function activate(key: string) {
  activeKey.value = key;
  const t = typeOf(key);
  if (t === 'person' || t === 'entity') {
    emit('request-person-lookup', {
      fieldGroup: 'exempt-private-company',
      key,
      defaultRole: defaultRoleForKey(key),
      lookupType: t === 'entity' ? 'entity' : 'officer',
    });
    inputKey.value = '';
  } else {
    inputKey.value = key;
    inputText.value = state.values[key] || '';
    if (t === 'date') dateMenu.value = true;
  }
}
function onPickDate(d: any) {
  const date = Array.isArray(d) ? d[0] : d;
  if (!date) return;
  const dt = new Date(date);
  inputText.value = Number.isNaN(dt.getTime())
    ? String(date)
    : `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
  dateMenu.value = false;
  commitInput();
}
function commitInput() {
  if (inputKey.value) setFieldValue(inputKey.value, inputText.value.trim());
  inputKey.value = ''; activeKey.value = '';
}
function skipInput() { inputKey.value = ''; activeKey.value = ''; }

const pendingPickValue = ref('');
watch(() => props.pendingPick, (p) => { pendingPickValue.value = p?.value || ''; });
function placePick(key: string): boolean {
  if (!pendingPickValue.value) return false;
  setFieldValue(key, pendingPickValue.value);
  pendingPickValue.value = '';
  return true;
}

const addrModal = ref(false);
const addrKey = ref('');
const addrLabel = ref('');
const addrInitial = ref<{ line?: string }>({});
function openAddress(key: string) {
  if (props.studio) { addrKey.value = key; emit('request-address-input', key); return; }
  addrKey.value = key;
  addrLabel.value = labelize(key);
  addrInitial.value = { line: state.values[key] || '' };
  addrModal.value = true;
}
function applyAddressTo(key: string, a: { line: string; town: string; state: string; postcode: string; country: string }) {
  if (!key) return;
  state.values[key] = composeMyAddressLine(a);
}
function applyAddress(a: { line: string; town: string; state: string; postcode: string; country: string }) {
  applyAddressTo(addrKey.value, a);
}
watch(() => props.appliedAddress, (a) => { if (a?.key) applyAddressTo(a.key, a); });

function flatAddr(v: any): string {
  return formatMyAddressLine(String(v ?? ''));
}

function pickStr(...vals: any[]): string {
  for (const v of vals) {
    const s = String(v ?? '').trim();
    if (s) return s;
  }
  return '';
}

function upsertSigner(key: string, a: any, name: string) {
  const roleMap: Record<string, { role: string; title: string }> = {
    director_name: { role: 'director', title: 'Director' },
    secretary_name: { role: 'secretary', title: 'Secretary' },
    audit_firm_name: { role: 'auditor', title: 'Auditor' },
  };
  const info = roleMap[key];
  if (!info || !name) return;
  const id = String(a?.contactCd || a?.officerIdx || `${info.role}-${name}`);
  const existing = state.signers.findIndex((s) => s.role === info.role);
  const entry: SignerEntry = {
    id,
    name,
    title: info.title,
    role: info.role,
    email: pickStr(a?.contactEmail, a?.email, a?.emailAddress),
    contactCd: a?.contactCd ? String(a.contactCd) : undefined,
    officerIdx: a?.officerIdx != null ? Number(a.officerIdx) : undefined,
    showTitle: true,
    _signerLayout: 'box',
  };
  if (existing >= 0) state.signers[existing] = entry;
  else state.signers.push(entry);
}

function applyPersonToField(key: string, a: any) {
  const name = a.name || a.contactName || [a.firstName, a.middleName, a.surname].filter(Boolean).join(' ') || '';
  state.values[key] = name;
  upsertSigner(key, a, name);

  if (key === 'secretary_name') {
    // OfficerDetail 정규화 키(licenseNumber / ssmPractisingCertificateNumber) 우선
    state.values.secretary_license_no = pickStr(
      a.licenseNumber, a.licenseNo, a.membershipNo, a.secretaryLicenseNo,
      a.LICENSE_NO, a.SECRETARY_LICENSE_NO, a.SecretaryLicenseNo, a.license_number,
    );
    state.values.secretary_practicing_cert_no = pickStr(
      a.ssmPractisingCertificateNumber, a.practicingCertNo, a.practicingCertificateNo,
      a.PRACTICING_CERT_NO, a.SecretaryCertificateNo, a.SECRETARY_CERTIFICATE_NO,
    );
  }
  if (key === 'lodger_name') {
    const nric = pickStr(a.nricNo, a.identificationNo, a.idNo, a.NRIC_NO, a.contactRegNo);
    if (nric) state.values.lodger_nric = nric;
    const addrs = Array.isArray(a.addresses) ? a.addresses : [];
    const addrFromArr = (x: any) => flatAddr(x?.composed || x?.value || x?.address || '');
    const fullAddr = a.residentialAddress || a.primaryAddress || '';
    const res = a.myResidential && !isEmptyMyAddress(a.myResidential) ? a.myResidential : parseMalaysianAddress(fullAddr);
    const fromParsed = !isEmptyMyAddress(res) ? composeMyAddressLine(res) : '';
    const addrLine = fromParsed || flatAddr(fullAddr) || addrFromArr(addrs[0]) || flatAddr(a.address || '');
    if (addrLine) state.values.lodger_address = addrLine;
    const phone = pickStr(a.phone, a.mobile, a.telNo, a.mobileNo);
    const email = pickStr(a.email, a.emailAddress, a.contactEmail);
    if (phone) state.values.lodger_phone = phone;
    if (email) state.values.lodger_email = email;
  }
  if (key === 'audit_firm_name') {
    state.values.audit_firm_name = name;
    state.values.audit_firm_number = pickStr(
      a.firmNo, a.FirmNo, a.FIRM_NO, a.officerFirmNo, a.contactProfileFirmNo,
    );
  }
}

watch(() => props.selectedPerson, (p) => {
  const ctx = props.activePersonContext;
  if (!p || !ctx || ctx.fieldGroup !== 'exempt-private-company' || !ctx.key) return;
  applyPersonToField(ctx.key, p as any);
});

provide(EPC_DOC_CTX, {
  valueOf: (k) => state.values[k] || '',
  labelOf: labelize,
  typeOf,
  activeKey,
  activate,
  setFieldValue,
  openAddress,
  entityCd,
  pendingPickValue,
  placePick,
});

/** Entity 변경 시 직책/사람 필드 잔존 방지 — ✦ 로만 채움 */
const OFFICER_FIELD_KEYS = [
  'director_name',
  'secretary_name',
  'secretary_license_no',
  'secretary_practicing_cert_no',
  'audit_firm_name',
  'audit_firm_number',
  'auditor_name',
  'audit_license_no',
  'lodger_name',
  'lodger_nric',
  'lodger_address',
  'lodger_phone',
  'lodger_email',
] as const;

function resetOfficerFields() {
  for (const k of OFFICER_FIELD_KEYS) state.values[k] = '';
  state.signers = [];
}

const entityPrefilled = ref('');
watch(
  () => [entityCd.value, props.entityData] as const,
  async ([cd]) => {
    if (!cd) {
      entityPrefilled.value = '';
      resetOfficerFields();
      state.values.company_name = '';
      state.values.company_registration_no = '';
      return;
    }
    if (entityPrefilled.value === cd) return;
    entityPrefilled.value = cd;
    resetOfficerFields();

    const put = (k: string, v: any, force = false) => {
      const s = String(v || '').trim();
      if (!s) return;
      if (force || !String(state.values[k] || '').trim()) state.values[k] = s;
    };
    put('company_name', props.entityData?.companyName || props.entityData?.entityName, true);
    put(
      'company_registration_no',
      formatMyCompanyRegNo(
        props.entityData?.registrationNo || props.entityData?.regNo || props.entityData?.REG_NO,
        props.entityData?.registrationNoOld || props.entityData?.regNoOld || props.entityData?.REG_NO_OLD,
      ),
      true,
    );
    try {
      const addrs = await fetchEntityAddresses(cd);
      const gov = (addrs as any)?.government || addrs;
      if (gov) {
        put('company_name', gov.companyName, false);
        put(
          'company_registration_no',
          formatMyCompanyRegNo(gov.registrationNo || gov.regNo, gov.regNoOld || gov.registrationNoOld),
          true,
        );
      }
    } catch { /* noop */ }
  },
  { immediate: true },
);

const overallProgress = computed(() => {
  const list = EPC_REQUIRED;
  if (!list.length) return 0;
  const filled = list.filter((k) => String(state.values[k] || '').trim()).length;
  return Math.round((filled / list.length) * 100);
});
watch(overallProgress, (v) => emit('progress-change', v), { immediate: true });

const editMode = computed(() => !!props.editTaskId);
const taskConfigSteps = ref<string[]>([]);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; });
watch(progress, (v) => { presetProgress.message = v; });
onMounted(async () => {
  const code = props.selection?.task?.TASK_CODE;
  if (code) {
    try {
      const cfg = await getTaskConfigByCode(code);
      taskConfigSteps.value = (cfg as any)?.processSteps || [];
    } catch { /* noop */ }
  }
});

const availableTemplates = computed(() => (props.selection?.documents || []).filter((d: any) => d.tmpId));

function tmplName(d: any): string {
  return String(d.templateName || d.docNm || d.name || d.fileName || d.orgFileName || d.docCode || '');
}
function templateFileLabel(d: any, fallback: string): string {
  const raw = tmplName(d).trim() || fallback;
  return raw.replace(/\.docx$/i, '').trim() || fallback;
}

type DocKind = 'cert' | 'auditors';
const DOC_SPECS: { kind: DocKind; re: RegExp; label: string }[] = [
  {
    kind: 'cert',
    re: /certificate.*exempt|exempt.*private.*company|section\s*260|lodgement\s*of\s*certificate/i,
    label: 'Certificate of Exempt Private Company (Section 260)',
  },
  {
    kind: 'auditors',
    re: /statement\s*by\s*auditors|section\s*261|auditors?\s*pursuant/i,
    label: 'Statement by Auditors (Section 261)',
  },
];

function resolveTemplates(): { tmpId: number; label: string; kind: DocKind }[] {
  const docs = availableTemplates.value as any[];
  if (!docs.length) return [];
  const used = new Set<number>();
  const out: { tmpId: number; label: string; kind: DocKind }[] = [];
  for (const spec of DOC_SPECS) {
    const hit = docs.find((d) => !used.has(d.tmpId) && spec.re.test(tmplName(d)));
    if (hit?.tmpId) {
      used.add(hit.tmpId);
      out.push({ tmpId: hit.tmpId, label: templateFileLabel(hit, spec.label), kind: spec.kind });
    }
  }
  for (const d of docs) {
    if (used.has(d.tmpId) || !d.tmpId) continue;
    const fallback = DOC_SPECS.find((s) => !out.some((o) => o.kind === s.kind));
    if (!fallback) break;
    used.add(d.tmpId);
    out.push({ tmpId: d.tmpId, label: templateFileLabel(d, fallback.label), kind: fallback.kind });
  }
  return out;
}

async function handleCreateTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) { progress.value = 'Please enter a Task Title.'; return; }

  const hits = resolveTemplates();
  if (!hits.length) {
    progress.value = '⚠ 연결된 문서 템플릿이 없습니다. 관리자에서 Exempt Private Company 문서 템플릿을 연결해 주세요.';
    return;
  }

  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing'),
    };
    const officerData = {
      entityCd: entityCd.value || null,
      presetForm: gatherState(),
      companyName: state.values.company_name || '',
      companyRegistrationNo: state.values.company_registration_no || '',
      fsMadeUpTo: state.values.fs_made_up_to || '',
      circulationDate: state.values.circulation_date || '',
      certificationDate: state.values.certification_date || '',
      directorName: state.values.director_name || '',
      secretaryName: state.values.secretary_name || '',
      auditorName: state.values.auditor_name || '',
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id'; creating.value = false; return; }
      await fetch(`/api/startTask/tasks/${taskId}/data`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskData: { formData: officerData } }),
      });
      await fetch(`/api/startTask/tasks/${taskId}/title`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskTitle: effectiveTitle.value }),
      });
    } else {
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value || null,
          taskTypeCd: props.selection.task.TASK_CODE,
          taskTypeName: props.selection.task.TASK_NAME,
          taskTitle: effectiveTitle.value, taskFlow, officerData,
        }),
      }).then((r) => r.json());
      taskId = taskRes?.data?.taskId || taskRes?.taskId;
      if (!taskId) {
        progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`;
        creating.value = false; return;
      }
    }

    progress.value = 'Preparing documents…';
    const formData = buildEpcVariables(state);
    const docsToSave = hits.map((h) => ({
      templateId: h.tmpId,
      label: h.label,
      personName: String(state.values.company_name || 'Company'),
      entityCd: entityCd.value || undefined,
      formData,
      signers: state.signers.length ? state.signers : undefined,
    }));
    try {
      const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
      }).then((r) => r.json());
      if (!saveRes?.success) progress.value = `⚠ Documents: ${saveRes?.message || 'unknown'}`;
    } catch (err: any) {
      console.warn('[Exempt Private Company] document save failed:', err);
    }

    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      });
    } catch { /* ignore */ }
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch { /* ignore */ }
    try {
      const [taskDetailRes, docListRes] = await Promise.all([
        fetch(`/api/startTask/tasks/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
        fetch(`/api/startTask/doc-cust-save/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
      ]);
      sessionStorage.setItem(`presetTaskDetailPrefetch:${taskId}`, JSON.stringify({ taskDetail: taskDetailRes, docList: docListRes }));
    } catch { /* ignore */ }

    emit('task-created', { taskId, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    console.error('[Exempt Private Company] create task 실패', e);
    progress.value = `Error: ${e?.message || e}`;
  } finally {
    creating.value = false;
  }
}

function gatherState() {
  return {
    state: JSON.parse(JSON.stringify(state)),
    companyName: state.values.company_name || '',
    companyRegistrationNo: state.values.company_registration_no || '',
    fsMadeUpTo: state.values.fs_made_up_to || '',
    circulationDate: state.values.circulation_date || '',
    certificationDate: state.values.certification_date || '',
    directorName: state.values.director_name || '',
    secretaryName: state.values.secretary_name || '',
    auditorName: state.values.auditor_name || '',
    signers: [...state.signers],
  };
}
function restoreState(s: any) {
  const src = s?.state || s?.presetForm?.state;
  if (src && typeof src === 'object') {
    const base = emptyEpcState();
    Object.assign(state, { ...base, ...src, values: { ...(src.values || {}) }, signers: Array.isArray(src.signers) ? src.signers : [] });
    for (const k of Object.keys(state.values)) {
      if (typeOf(k) === 'address' && state.values[k]) {
        state.values[k] = formatMyAddressLine(state.values[k]);
      }
    }
  }
  if (Array.isArray(s?.signers)) state.signers = s.signers;
}
defineExpose({ gatherState, restoreState, createTask: handleCreateTask });
</script>

<template>
  <div class="otc-panel" :class="{ 'otc-studio': studio }">
    <div v-if="!studio" class="co-header">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME }}</div>
        <div class="co-subtitle">Exempt Private Company Certificate (Section 260 &amp; 261)</div>
      </div>
      <v-btn variant="text" size="small" class="text-none" @click="emit('back')"><v-icon start size="16">mdi-arrow-left</v-icon>Back</v-btn>
    </div>

    <div v-if="!studio" class="co-title-row">
      <span class="ct-label">Task Title</span>
      <v-text-field v-model="titleModel" placeholder="e.g. Exempt Private Company Certificate — Tilkoblet" variant="outlined" density="compact" hide-details class="ct-input" />
    </div>

    <div v-if="!studio" class="co-entity">
      <v-icon size="18" color="primary">mdi-domain</v-icon>
      <span>{{ entityData?.companyName || entityData?.entityName || 'Entity' }}</span>
      <v-btn variant="tonal" size="x-small" class="text-none ml-auto" @click="emit('change-entity')">Change</v-btn>
    </div>

    <div v-if="inputKey" class="co-inputbar">
      <div class="lbl">{{ labelize(inputKey) }} <span v-if="typeOf(inputKey) === 'date'" class="opt">(optional)</span></div>
      <div class="row">
        <v-menu v-if="typeOf(inputKey) === 'date'" v-model="dateMenu" :close-on-content-click="false">
          <template #activator="{ props: mp }">
            <v-text-field v-bind="mp" v-model="inputText" placeholder="DD/MM/YYYY" variant="outlined" density="compact" hide-details append-inner-icon="mdi-calendar" />
          </template>
          <v-date-picker @update:model-value="onPickDate" />
        </v-menu>
        <v-text-field v-else v-model="inputText" :placeholder="labelize(inputKey)" variant="outlined" density="compact" hide-details autofocus @keydown.enter="commitInput" />
        <v-btn color="primary" size="small" variant="flat" @click="commitInput">Set</v-btn>
        <v-btn v-if="typeOf(inputKey) === 'date'" variant="text" size="small" @click="skipInput">Skip</v-btn>
      </div>
    </div>

    <div class="co-doc-body">
      <div class="epc-pkg">
        <div class="pkg-doc">
          <div class="pkg-doclabel">
            <span class="ic">📜</span>
            <span class="nm">Certificate of Exempt Private Company (Section 260)</span>
            <span class="no">Doc 1 / 2</span>
          </div>
          <div class="pkg-paper">
            <EpcCertificateDoc />
          </div>
        </div>

        <div class="pkg-doc">
          <div class="pkg-doclabel">
            <span class="ic">🧾</span>
            <span class="nm">Statement by Auditors (Section 261)</span>
            <span class="no">Doc 2 / 2</span>
          </div>
          <div class="pkg-paper">
            <EpcAuditorsStatementDoc />
          </div>
        </div>
      </div>
    </div>

    <div v-if="!studio || progress" class="co-footer">
      <v-btn v-if="!studio" variant="outlined" class="text-none" @click="emit('back')">Back</v-btn>
      <div class="co-foot-right">
        <span v-if="progress" class="co-progress" :class="{ err: progress.startsWith('Error') || progress.includes('⚠') }">{{ progress }}</span>
        <v-btn
          v-if="!studio"
          color="primary"
          class="text-none"
          :loading="creating"
          :disabled="creating"
          :prepend-icon="editMode ? 'mdi-content-save' : 'mdi-check'"
          @click="handleCreateTask"
        >{{ editMode ? 'Update Task' : 'Create Task' }}</v-btn>
      </div>
    </div>

    <AddressInputModal v-model="addrModal" :label="addrLabel" :has-components="false" :initial="addrInitial" @apply="applyAddress" />
  </div>
</template>

<style scoped>
.otc-panel { display:flex; flex-direction:column; }
.otc-studio { flex:0 0 auto; width:min(1000px, 100%); background:transparent; }
.otc-studio .co-doc-body { background:transparent; padding:0; }
.co-header { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-bottom:1px solid #eef0f6; }
.co-title { font-weight:700; font-size:15px; } .co-subtitle { font-size:12px; color:#8a94a6; }
.co-title-row { display:flex; align-items:center; gap:10px; padding:10px 14px; border-bottom:1px solid #eef0f6; }
.co-title-row .ct-label { font-size:12px; font-weight:700; color:#5b6472; white-space:nowrap; }
.co-title-row .ct-input { max-width:520px; }
.co-entity { display:flex; align-items:center; gap:8px; background:#f0f5ff; padding:8px 14px; font-size:13px; }
.co-inputbar { padding:10px 14px; background:#fafbfe; border-bottom:1px solid #eef0f6; position:sticky; top:0; z-index:5; }
.co-inputbar .lbl { font-size:12px; font-weight:700; color:#1B84FF; margin-bottom:6px; } .co-inputbar .opt { color:#8a94a6; font-weight:400; }
.co-inputbar .row { display:flex; gap:8px; align-items:center; }
.co-doc-body { padding:16px 12px 28px; }
.co-footer { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-top:1px solid #eef0f6; gap:12px; }
.co-foot-right { display:flex; align-items:center; gap:12px; margin-left:auto; }
.co-progress { font-size:12px; color:#5b6472; } .co-progress.err { color:#c0392b; }
.epc-pkg { display:flex; flex-direction:column; gap:28px; width:100%; max-width:900px; margin:0 auto; }
.pkg-doc { width: 100%; margin: 0 auto; }
.pkg-doclabel { display: flex; align-items: center; gap: 7px; padding: 0 4px 9px; }
.pkg-doclabel .ic { font-size: 14px; line-height: 1; }
.pkg-doclabel .nm { font-size: 12px; font-weight: 800; color: #42506a; letter-spacing: .01em; }
.pkg-doclabel .no { margin-left: auto; font-size: 10.5px; font-weight: 800; color: #9aa6bd; white-space: nowrap; }
.pkg-paper {
  background: #fff; padding: 34px 38px;
  box-shadow: 0 4px 24px rgba(20, 30, 60, .12);
  font-family: Arial, 'Malgun Gothic', sans-serif;
  color: #111; font-size: 12px; line-height: 1.4; border-radius: 2px;
}
</style>
